import React from "react";

import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { IProduct } from "../../../models/catalogue";
import { IProductCategoryID } from "../../../models/nominals";
import { BreakPoint } from "../../../models/screen";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { GridContent } from "../elements/GridContent";
import { SideNav } from "../elements/SideNav";
import { TopNav } from "../elements/TopNav";
import { GridFilterConfig } from "../filters";
import { startHistoryListener } from "../history";
import { GridVariant } from "../models";
import { GridSorterConfig } from "../sorters";
import { loadProducts } from "../thunks";

import styles from "./ProductGrid.module.scss";

interface IProps {
    gridVariant: GridVariant;
    productCategoryID: IProductCategoryID;
    filters: GridFilterConfig[];
    sorters?: GridSorterConfig[];
    hideSort?: boolean;
    disableHoverOverlay?: boolean;
    buildExternalCallout?: (rootProduct: IProduct) => React.ReactNode;
}

const isMobileOrTabletWidth = (): boolean => {
    const currentBreakpoint = getViewportBreakpoint();
    return currentBreakpoint < BreakPoint.LARGE;
};

export const ProductGrid = (props: IProps): React.ReactNode => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector((state) => state.productgrid2.isLoading);

    React.useEffect(() => {
        startHistoryListener(dispatch);
    }, [dispatch]);

    React.useEffect(() => {
        void dispatch(loadProducts(props.productCategoryID));
    }, [dispatch, props.productCategoryID]);

    let content: React.ReactNode | null = null;
    if (isLoading) {
        content = <LoadingSpinner />;
    } else {
        content = (
            <>
                <TopNav
                    gridVariant={props.gridVariant}
                    filters={props.filters}
                    sorters={props.sorters}
                    hideSort={props.hideSort}
                />
                <div className={styles.contentContainer}>
                    {!isMobileOrTabletWidth() && (
                        <SideNav
                            gridVariant={props.gridVariant}
                            filters={props.filters}
                        />
                    )}
                    {/* TODO(i18n): cardType="grid-card" should be a technical key, but verify it's not displayed to users */}
                    <GridContent
                        gridVariant={props.gridVariant}
                        cardType="grid-card"
                        filters={props.filters}
                        sorters={props.sorters}
                        disableHoverOverlay={props.disableHoverOverlay}
                        buildExternalCallout={props.buildExternalCallout}
                    />
                </div>
            </>
        );
    }
    return <div className={styles.container}>{content}</div>;
};
